import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { messengerApi } from '@/features/messenger/api/messenger.api'
import {
  findDeliveredUnsent,
  newPendingId,
  toFailedId,
} from '@/features/messenger/utils/localMessage'
import { useAuthStore } from '@/features/auth/stores/auth.store'
import { renderTemplateBody } from '@/features/messenger/utils/waTemplate'
import { mergeFreshPage } from '@/features/messenger/utils/mergeFreshPage'
import type {
  DeviceInfo,
  ChatInfo,
  MessageInfo,
  SendTextRequest,
  SendImageRequest,
  SendDocumentRequest,
  SendWhatsAppTemplateRequest,
  WhatsAppTemplate,
  WsNewMessagePayload,
  WsMessageStatusPayload,
  WsMessageReactionPayload,
  WsMessageMediaUpdatePayload,
  WhatsAppSendFailure,
} from '@/features/messenger/types/messenger.types'
import type { WsProviderStatusPayload } from '@/features/messenger/types/telegram.types'

// Окно свободной переписки WhatsApp Business — как waba.WindowDuration в messenger-providers.
const WA_WINDOW_MS = 24 * 60 * 60 * 1000

/**
 * DEV-2418: провал отправки раньше только убирал пузырь — менеджер не узнавал,
 * что сообщение не ушло. 403 сюда не относим: его тостом «Права изменились»
 * уже показывает интерцептор.
 */
export function classifyWhatsAppSendError(e: unknown): WhatsAppSendFailure | null {
  const response = (e as { response?: { status?: number; data?: { message?: string } } } | null)
    ?.response
  if (response?.status === 403) return null
  const code = response?.data?.message === 'WABA_PAYMENT_REQUIRED' ? 'payment_required' : 'generic'
  return { code, at: Date.now() }
}

export const useMessengerStore = defineStore('messenger', () => {
  // Device state (populated via WebSocket)
  const devices = ref<DeviceInfo[]>([])

  // Флаг: первый LIST_DEVICES получен через WS
  const isInitialWsLoaded = ref(false)

  // Общий номер WhatsApp Business (DEV-1683): сервер шлёт всю переписку через него.
  // Узнаём из PROVIDER_STATUS. Своих номеров по QR больше нет (DEV-2456), поэтому
  // без общего номера WhatsApp в CRM недоступен.
  const wabaEnabled = ref(false)
  const isProviderStatusLoaded = ref(false)
  const canChat = computed(() => wabaEnabled.value)
  // Ждём и снимок, иначе до его прихода мелькнёт «WhatsApp не подключён».
  const isWhatsAppReady = computed(() => isInitialWsLoaded.value && isProviderStatusLoaded.value)

  function applyProviderStatus(snapshot: WsProviderStatusPayload | null) {
    wabaEnabled.value = !!snapshot?.waba
    isProviderStatusLoaded.value = true
  }

  // Chats list
  const chats = ref<ChatInfo[]>([])
  const chatsTotal = ref(0)
  const isLoadingChats = ref(false)

  // Последний провал отправки — вкладка показывает по нему тост (DEV-2418).
  const sendFailure = ref<WhatsAppSendFailure | null>(null)

  // Active chat / messages
  const currentChatJid = ref<string | null>(null)
  let currentChatClientId: string | undefined
  const currentChatInfo = ref<ChatInfo | null>(null)
  const messages = ref<MessageInfo[]>([])
  const messagesTotal = ref(0)
  const isLoadingMessages = ref(false)

  // DEV-1686: шаблоны WhatsApp Business (вне окна 24ч писать можно только ими)
  const templates = ref<WhatsAppTemplate[]>([])
  const isLoadingTemplates = ref(false)

  // Срок окна 24ч текущего чата (мс). Приходит с лентой, но входящее из сокета
  // продлевает его само: иначе ввод открывался только перезагрузкой (DEV-2437).
  const waWindowExpiresAt = ref<number | null>(null)

  function extendWaWindow(expiresAt: number) {
    if (Number.isNaN(expiresAt)) return
    if (waWindowExpiresAt.value === null || expiresAt > waWindowExpiresAt.value) {
      waWindowExpiresAt.value = expiresAt
    }
  }

  // Окно — правило только WhatsApp Business: у личного WhatsApp ввод не блокируем.
  function isComposerLocked(now: number): boolean {
    if (!wabaEnabled.value) return false
    return waWindowExpiresAt.value === null || waWindowExpiresAt.value <= now
  }

  // Errors
  const error = ref<string | null>(null)

  // Signal: инкрементируется при получении CLIENT_FILE_ADDED WS-события
  const clientFileAddedSignal = ref(0)

  function updateDevicesFromWs(list: DeviceInfo[] | null) {
    // Без устройств сервер шлёт null, а не [].
    devices.value = list ?? []
    isInitialWsLoaded.value = true
  }

  async function loadChats(params?: { search?: string; offset?: number; limit?: number }) {
    isLoadingChats.value = true
    error.value = null
    try {
      const response = await messengerApi.getChats({
        limit: params?.limit ?? 50,
        offset: params?.offset ?? 0,
        search: params?.search,
      })
      chats.value = response.data
      chatsTotal.value = Number(response.pagination?.total) || 0
    } catch (e) {
      error.value = String(e)
    } finally {
      isLoadingChats.value = false
    }
  }

  async function loadMessages(jid: string, params?: { offset?: number; client_id?: string }) {
    isLoadingMessages.value = true
    error.value = null
    try {
      const response = await messengerApi.getMessages(jid, {
        limit: 50,
        offset: params?.offset ?? 0,
        client_id: params?.client_id,
      })
      // API returns newest-first (DESC) — reverse to chronological (ASC)
      const data = (response.data ?? []).slice().reverse()
      if (params?.offset && params.offset > 0) {
        messages.value = [...data, ...messages.value]
      } else {
        messages.value = data
      }
      messagesTotal.value = Number(response.pagination?.total) || 0
      currentChatInfo.value = response.chatInfo ?? null
      if (response.chatInfo?.waWindowExpiresAt) {
        extendWaWindow(Date.parse(response.chatInfo.waWindowExpiresAt))
      }
    } catch (e) {
      error.value = String(e)
    } finally {
      isLoadingMessages.value = false
    }
  }

  /**
   * DEV-2502. Досинхронизация открытого чата: события в разрыв сокета терялись, а
   * перечитывалась после переподключения только лента Telegram.
   */
  async function reloadCurrentChat() {
    const jid = currentChatJid.value
    if (!jid) return
    try {
      const response = await messengerApi.getMessages(jid, {
        limit: 50,
        offset: 0,
        client_id: currentChatClientId,
      })
      if (currentChatJid.value !== jid) return
      messages.value = mergeFreshPage(
        messages.value,
        (response.data ?? []).slice().reverse(),
        useAuthStore().user?.id,
      )
      messagesTotal.value = Number(response.pagination?.total) || messagesTotal.value
      if (response.chatInfo) currentChatInfo.value = response.chatInfo
      if (response.chatInfo?.waWindowExpiresAt) {
        extendWaWindow(Date.parse(response.chatInfo.waWindowExpiresAt))
      }
    } catch (e) {
      console.warn('[messenger] досинхронизация чата не удалась:', e)
    }
  }

  async function setCurrentChat(jid: string, clientId?: string) {
    currentChatJid.value = jid
    currentChatClientId = clientId
    messages.value = []
    messagesTotal.value = 0
    currentChatInfo.value = null
    waWindowExpiresAt.value = null
    await loadMessages(jid, clientId ? { client_id: clientId } : undefined)
  }

  function resetChat() {
    currentChatJid.value = null
    messages.value = []
    messagesTotal.value = 0
    currentChatInfo.value = null
    waWindowExpiresAt.value = null
  }

  async function sendText(req: SendTextRequest) {
    // Optimistic update
    const now = new Date().toISOString()
    const optimistic: MessageInfo = {
      id: newPendingId(),
      chatJid: req.phone,
      senderJid: '',
      content: req.message,
      timestamp: now,
      isFromMe: true,
      mediaType: 'text',
      status: 'pending',
      createdAt: now,
      updatedAt: now,
    }
    messages.value = [...messages.value, optimistic]
    try {
      await messengerApi.sendText(req)
    } catch (e) {
      // DEV-2492. Текст не теряем: пузырь остаётся «Не отправлено» с «Повторить».
      messages.value = messages.value.map((m) =>
        m.id === optimistic.id ? { ...m, id: toFailedId(m.id), status: 'failed' } : m,
      )
      error.value = String(e)
      sendFailure.value = classifyWhatsAppSendError(e)
    }
  }

  // DEV-2492. «Повторить» убирает неотправленный пузырь — текст уйдёт новой отправкой.
  function discardMessage(id: string) {
    messages.value = messages.value.filter((m) => m.id !== id)
  }

  async function loadTemplates() {
    isLoadingTemplates.value = true
    error.value = null
    try {
      const response = await messengerApi.listTemplates()
      templates.value = response.data ?? []
    } catch (e) {
      error.value = String(e)
    } finally {
      isLoadingTemplates.value = false
    }
  }

  /**
   * DEV-1686: отправка шаблона вне окна 24ч. В ленту кладём отрисованное тело
   * шаблона — WS-событие о доставке подменит его тем же текстом.
   * Возвращает признак успеха: по нему вызывающий закрывает модалку.
   */
  async function sendTemplate(req: SendWhatsAppTemplateRequest): Promise<boolean> {
    const template = templates.value.find(
      (tpl) => tpl.name === req.name && tpl.language === req.language,
    )
    const now = new Date().toISOString()
    const optimistic: MessageInfo = {
      id: newPendingId(),
      chatJid: req.phone,
      senderJid: '',
      content: renderTemplateBody(template?.body, req.parameters ?? []),
      timestamp: now,
      isFromMe: true,
      mediaType: 'text',
      status: 'pending',
      createdAt: now,
      updatedAt: now,
    }
    messages.value = [...messages.value, optimistic]
    try {
      await messengerApi.sendTemplate(req)
      return true
    } catch (e) {
      messages.value = messages.value.filter((m) => m.id !== optimistic.id)
      error.value = String(e)
      sendFailure.value = classifyWhatsAppSendError(e)
      return false
    }
  }

  async function sendImage(req: SendImageRequest) {
    const now = new Date().toISOString()
    const optimistic: MessageInfo = {
      id: newPendingId(),
      chatJid: req.phone,
      senderJid: '',
      content: req.caption || '',
      timestamp: now,
      isFromMe: true,
      mediaType: 'image',
      status: 'pending',
      createdAt: now,
      updatedAt: now,
    }
    messages.value = [...messages.value, optimistic]
    try {
      await messengerApi.sendImage(req)
    } catch (e) {
      messages.value = messages.value.filter((m) => m.id !== optimistic.id)
      error.value = String(e)
      sendFailure.value = classifyWhatsAppSendError(e)
    }
  }

  async function sendDocument(req: SendDocumentRequest) {
    const now = new Date().toISOString()
    const optimistic: MessageInfo = {
      id: newPendingId(),
      chatJid: req.phone,
      senderJid: '',
      content: req.file_name || '',
      timestamp: now,
      isFromMe: true,
      mediaType: 'document',
      status: 'pending',
      createdAt: now,
      updatedAt: now,
    }
    messages.value = [...messages.value, optimistic]
    try {
      await messengerApi.sendDocument(req)
    } catch (e) {
      messages.value = messages.value.filter((m) => m.id !== optimistic.id)
      error.value = String(e)
      sendFailure.value = classifyWhatsAppSendError(e)
    }
  }

  /** Map WS snake_case payload → camelCase MessageInfo */
  function addMessageFromWs(payload: WsNewMessagePayload) {
    if (payload.chat_jid !== currentChatJid.value) return

    // Звонок в ленте — не сообщение клиента: окно на сервере он не открывает (DEV-2443).
    if (!payload.is_from_me && payload.media_type !== 'call') {
      extendWaWindow(Date.parse(payload.timestamp) + WA_WINDOW_MS)
    }

    const alreadyExists = messages.value.some((m) => m.id === payload.id)
    if (alreadyExists) return

    const now = payload.timestamp
    const msg: MessageInfo = {
      id: payload.id,
      chatJid: payload.chat_jid,
      senderJid: payload.sender_jid,
      content: payload.content,
      timestamp: now,
      isFromMe: payload.is_from_me,
      mediaType: payload.media_type,
      status: 'delivered',
      createdAt: now,
      updatedAt: now,
      authorType: payload.author_type,
      authorManagerId: payload.author_manager_id,
    }

    if (payload.is_from_me) {
      let pendingIdx = messages.value.findIndex(
        (m) => m.id.startsWith('pending-') && m.content === payload.content,
      )
      // DEV-2492. Запрос упал, а сообщение ушло: своё эхо подтверждает «Не отправлено».
      // Своё — личный номер (автора нет) или я сам на общем номере.
      const ownEcho =
        !payload.author_manager_id || payload.author_manager_id === useAuthStore().user?.id
      if (pendingIdx === -1 && ownEcho) {
        pendingIdx = findDeliveredUnsent(messages.value, payload.content, payload.timestamp)
      }
      if (pendingIdx !== -1) {
        messages.value = [
          ...messages.value.slice(0, pendingIdx),
          msg,
          ...messages.value.slice(pendingIdx + 1),
        ]
        return
      }
    }

    messages.value = [...messages.value, msg]
  }

  function updateMessageStatus(payload: WsMessageStatusPayload) {
    if (payload.chat_jid !== currentChatJid.value) return

    const idx = messages.value.findIndex((m) => m.id === payload.id)
    if (idx === -1) return

    const msg = messages.value[idx]
    if (!msg) return

    messages.value = [
      ...messages.value.slice(0, idx),
      { ...msg, status: payload.status },
      ...messages.value.slice(idx + 1),
    ]
  }

  function updateMessageReaction(payload: WsMessageReactionPayload) {
    if (payload.chat_jid !== currentChatJid.value) return

    const idx = messages.value.findIndex((m) => m.id === payload.target_message_id)
    if (idx === -1) return

    const msg = messages.value[idx]
    if (!msg) return
    const existing = (msg.reactions ?? []).filter((r) => r.senderJid !== payload.sender_jid)
    const updated = payload.emoji
      ? [
          ...existing,
          {
            emoji: payload.emoji,
            senderJid: payload.sender_jid,
            isFromMe: payload.is_from_me,
            timestamp: payload.timestamp,
          },
        ]
      : existing

    messages.value = [
      ...messages.value.slice(0, idx),
      { ...msg, reactions: updated },
      ...messages.value.slice(idx + 1),
    ]
  }

  function notifyClientFileAdded() {
    clientFileAddedSignal.value++
  }

  async function sendReaction(phone: string, messageId: string, emoji: string) {
    // Optimistic update
    const idx = messages.value.findIndex((m) => m.id === messageId)
    if (idx === -1) return

    const msg = messages.value[idx]
    if (!msg) return
    const existing = (msg.reactions ?? []).filter((r) => r.isFromMe !== true)
    const optimistic = emoji
      ? [
          ...existing,
          {
            emoji,
            senderJid: '',
            isFromMe: true,
            timestamp: new Date().toISOString(),
          },
        ]
      : existing

    messages.value = [
      ...messages.value.slice(0, idx),
      { ...msg, reactions: optimistic },
      ...messages.value.slice(idx + 1),
    ]

    try {
      await messengerApi.reactMessage({ phone, message_id: messageId, emoji })
    } catch (e) {
      // Revert optimistic update on failure
      messages.value = [
        ...messages.value.slice(0, idx),
        { ...msg },
        ...messages.value.slice(idx + 1),
      ]
      error.value = String(e)
    }
  }

  function updateMessageMedia(payload: WsMessageMediaUpdatePayload) {
    if (payload.chat_jid !== currentChatJid.value) return

    const idx = messages.value.findIndex((m) => m.id === payload.id)
    if (idx === -1) return

    const msg = messages.value[idx]
    if (!msg) return
    const updated: Partial<MessageInfo> = { mediaType: payload.media_type }
    if (payload.url) updated.url = payload.url
    if (payload.filename) updated.filename = payload.filename
    if (payload.file_length) updated.fileLength = payload.file_length

    messages.value = [
      ...messages.value.slice(0, idx),
      { ...msg, ...updated },
      ...messages.value.slice(idx + 1),
    ]
  }

  return {
    devices,
    isInitialWsLoaded,
    wabaEnabled,
    canChat,
    isWhatsAppReady,
    applyProviderStatus,
    isComposerLocked,
    chats,
    chatsTotal,
    isLoadingChats,
    currentChatJid,
    currentChatInfo,
    messages,
    messagesTotal,
    isLoadingMessages,
    templates,
    isLoadingTemplates,
    error,
    sendFailure,
    resetChat,
    updateDevicesFromWs,
    loadChats,
    loadMessages,
    reloadCurrentChat,
    setCurrentChat,
    sendText,
    discardMessage,
    sendImage,
    sendDocument,
    sendReaction,
    loadTemplates,
    sendTemplate,
    addMessageFromWs,
    updateMessageStatus,
    updateMessageReaction,
    updateMessageMedia,
    clientFileAddedSignal,
    notifyClientFileAdded,
  }
})
